var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Icon from 'ziphy-web-shared/basic/assets/images/Icon';
import { useMultiSelect, } from 'ziphy-web-shared/basic/lib/forms/fields';
import { Dropdown, DropdownContent, useDropdown, } from 'ziphy-web-shared/basic/lib/functional';
import { $loader, globalLoaderHandlers } from 'ziphy-web-shared/basic/utils';
import { formatDate } from 'ziphy-web-shared/basic/utils/time';
import FieldWrapper from '../_components/FieldWrapper';
import { useFieldWrapper } from '../_components/FieldWrapper.hooks';
import styles from './Select.module.scss';
function Select(_a) {
    var { field, onClick, onClear, className, 
    //
    options = [], items = options, multi = false, allowUnselect = false, allOption, otherField = {}, 
    //
    visibleCount = 1, separator = ', ', search, 
    //
    direction, popupMaxWidth = 0, popupClassName } = _a, 
    //
    buttonProps = __rest(_a, ["field", "onClick", "onClear", "className", "options", "items", "multi", "allowUnselect", "allOption", "otherField", "visibleCount", "separator", "search", "direction", "popupMaxWidth", "popupClassName"]);
    const { t } = useTranslation();
    const { selectedOptions, clearValue, handleToggleSelect, handleOtherFieldChange } = useMultiSelect({
        value: typeof field.value === 'undefined' ? '' : field.value,
        onChange: field.onChange,
        isInvalid: field.isInvalid,
        multi,
        allowUnselect,
        disabled: buttonProps.disabled,
    });
    const dropdownItems = useMemo(() => {
        const normalizedOptions = (items === null || items === void 0 ? void 0 : items.length) === 0
            ? []
            : 'items' in items[0]
                ? items
                : [{ items: items }];
        const result = normalizedOptions.map((group) => ({
            label: group.label,
            items: group.items.map((option) => ({
                id: option.id,
                label: option.text,
                description: option.description,
                selected: selectedOptions.has(option.id),
                disabled: option.disabled,
                onClick: () => __awaiter(this, void 0, void 0, function* () {
                    handleToggleSelect(option);
                    function handleFinish(val) {
                        if (val.id !== option.id)
                            return;
                    }
                    if (option.onClick) {
                        $loader.subscribe(globalLoaderHandlers.DROPDOWN_CLICK, handleFinish);
                        yield option.onClick();
                    }
                    else {
                        handleFinish(option);
                    }
                }),
            })),
        }));
        if (allOption) {
            result.unshift({
                items: [
                    {
                        id: 'all_options',
                        label: allOption,
                        selected: selectedOptions.size === 0,
                        disabled: false,
                        onClick: () => handleOnClear(!multi),
                    },
                ],
            });
        }
        return result;
    }, [items, selectedOptions, handleToggleSelect, allOption]);
    const { dropdownRef, searchString, setSearchString, filteredItems, totalFilteredItems } = useDropdown(dropdownItems);
    const otherSelected = useMemo(() => {
        const flatItems = dropdownItems.flatMap((group) => group.items);
        const key = Array.from(selectedOptions.keys()).find((id) => !flatItems.find((item) => item.id === id));
        return selectedOptions.get(key);
    }, [selectedOptions, dropdownItems]);
    const otherFieldProps = useMemo(() => {
        if (otherSelected || otherField.element) {
            if (otherField.element === 'date') {
                return Object.assign(Object.assign({}, otherField), { element: 'date', field: Object.assign(Object.assign({}, (otherField.field || {})), { value: (otherSelected === null || otherSelected === void 0 ? void 0 : otherSelected.id) || '', onChange: (v) => {
                            var _a;
                            handleOtherFieldChange(otherSelected === null || otherSelected === void 0 ? void 0 : otherSelected.id, v);
                            if (!multi)
                                (_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.setIsOpen(false);
                        } }) });
            }
            return Object.assign(Object.assign({}, otherField), { element: 'input', field: Object.assign(Object.assign({}, (otherField.field || {})), { value: (otherSelected === null || otherSelected === void 0 ? void 0 : otherSelected.id) || '', onChange: (v) => {
                        var _a;
                        handleOtherFieldChange(otherSelected === null || otherSelected === void 0 ? void 0 : otherSelected.id, v);
                        if (!multi)
                            (_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.setIsOpen(false);
                    } }), submitMode: multi ? 'update-on-change' : 'update-on-submit' });
        }
    }, [otherSelected, otherField, multi]);
    const multiselectPreview = useMemo(() => {
        const flatItems = dropdownItems.flatMap((group) => group.items);
        const result = Array.from(selectedOptions.keys())
            .map((id) => {
            var _a;
            const label = (_a = flatItems.find((item) => item.id === id)) === null || _a === void 0 ? void 0 : _a.label;
            if (label)
                return label;
            if (otherField.element === 'date')
                return formatDate(id);
            return id;
        })
            .filter(Boolean);
        if (result.length === 0)
            return allOption !== null && allOption !== void 0 ? allOption : '';
        if (result.length <= visibleCount)
            return result.join(separator);
        return t('label.selected_preview', { selectedCount: result.length });
    }, [selectedOptions, otherSelected, dropdownItems]);
    function handleOnClear(close = true) {
        var _a;
        onClear === null || onClear === void 0 ? void 0 : onClear();
        field.onChange(clearValue);
        if (close) {
            (_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.setIsOpen(false);
        }
    }
    const SelectButton = () => {
        var _a, _b, _c;
        const FW = useFieldWrapper(Object.assign(Object.assign({}, buttonProps), { field: {
                value: field.value,
                isInvalid: field.isInvalid,
            }, fieldRef: (_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.fieldRef, onClick: () => {
                var _a;
                onClick === null || onClick === void 0 ? void 0 : onClick();
                (_a = dropdownRef.current) === null || _a === void 0 ? void 0 : _a.onToggle();
            }, onClear: () => handleOnClear(), innerClassName: classNames(buttonProps.innerClassName, styles.field), rightElement: (_jsx(Icon, { type: 'down', className: classNames(styles.icon, ((_b = dropdownRef.current) === null || _b === void 0 ? void 0 : _b.isOpen) && styles.isOpen) })) }));
        return (_jsx(FieldWrapper, Object.assign({ element: 'input', props: Object.assign(Object.assign({}, FW), { isFocused: FW.isFocused || ((_c = dropdownRef.current) === null || _c === void 0 ? void 0 : _c.isOpen) }) }, { children: multiselectPreview || _jsx("div", Object.assign({ className: styles.placeholder }, { children: FW.placeholder })) })));
    };
    return (_jsx(Dropdown, Object.assign({ ref: dropdownRef, className: classNames(styles.select, className), classNamePopup: popupClassName, renderButton: SelectButton, maxWidth: popupMaxWidth, direction: direction, resizeDeps: [totalFilteredItems, selectedOptions.size] }, { children: _jsx(DropdownContent, { searchField: search === true || search === 'popup'
                ? {
                    value: searchString,
                    onChange: setSearchString,
                }
                : undefined, items: filteredItems, isMulti: multi, otherField: otherFieldProps }) })));
}
export default observer(Select);
